import { ISelectOption, Select } from '@salecycle/ui-component-library';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useParams } from 'react-router';
import { getSubjectLines } from 'sections/design/template-management/sections/template-list/utils/queries';
import { useSendEmail } from '../hooks/useSendEmail';
import { getFirstNonEmptyValue } from 'sections/utils/objectHelpers';

export const SubjectLineSelect = ({ id }) => {
  const { t, i18n } = useTranslation('campaigns');
  const { siteId } = useParams<{ siteId: string }>();

  const { getSubjectLine, setSubjectLine } = useSendEmail();

  const subjectLine = useMemo(() => getSubjectLine(id), [getSubjectLine, id]);

  const { isLoading, data } = useQuery(['subjectLines'], () => getSubjectLines({ siteId, t }));

  const defaultSelectedOption = useMemo(() => {
    if (subjectLine) {
      const label: ISelectOption<string> = {
        value: subjectLine?.id,
        label: subjectLine?.label,
      };

      return label;
    }
  }, [subjectLine]);

  const [selectedOption, setSelectedOption] = useState(defaultSelectedOption);

  useEffect(() => {
    setSelectedOption(defaultSelectedOption);
  }, [defaultSelectedOption]);

  const options: ISelectOption<string>[] = useMemo(() => {
    if (!isLoading && data) {
      return data.map(subjectLine => ({
        value: subjectLine.id as string,
        label: getFirstNonEmptyValue(subjectLine.subjectLines, i18n.language),
      }));
    }
  }, [data, i18n.language, isLoading]);

  const changeOption = useCallback(
    (option: ISelectOption<string>) => {
      setSelectedOption(option);
      setSubjectLine({ id, subjectLineId: option.value, subjectLineLabel: option.label });
    },
    [id, setSubjectLine],
  );

  return (
    <Select
      ariaLabel={t('onsite.editor.workflow.sendEmailMessage.drawer.subjectLabel')}
      label={t('onsite.editor.workflow.sendEmailMessage.drawer.subjectLabel')}
      placeholder={t('onsite.editor.workflow.sendEmailMessage.drawer.subjectLabelPlaceholder')}
      options={options}
      value={!!selectedOption?.value ? selectedOption : null}
      onChange={changeOption}
    />
  );
};
